import { get, writable } from 'svelte/store';

import { AssetsService } from '@client/services/assets';

import { activeDomain } from '@client/stores/domain-settings';

export const loadingAssets = writable(true);

const createAssetStore = (fetchFn, filterByMimeType = false) => {
  const assetStore = writable([]);

  let loaded = false;
  let lastDomain = get(activeDomain);

  const fetchAssets = async () => {
    loadingAssets.set(true);

    try {
      const response = await fetchFn();
      const items = response.filter((item) => !filterByMimeType || item.mimetype?.startsWith('image/'));

      assetStore.set(items);
    } catch (err) {
      console.error('Error fetching assets:', err);
      assetStore.set([]);
    } finally {
      loadingAssets.set(false);
    }
  };

  activeDomain.subscribe((newDomain) => {
    if (newDomain !== lastDomain) {
      loaded = false;
      lastDomain = newDomain;
      assetStore.set([]);
    }
  });

  return {
    subscribe(run) {
      if (!loaded) {
        loaded = true;
        fetchAssets();
      }

      return assetStore.subscribe(run);
    },
    update: assetStore.update
  };
};

export const images = createAssetStore(AssetsService.getAllImages, true);
export const documents = createAssetStore(AssetsService.getAllDocuments, false);
export const videos = createAssetStore(AssetsService.getAllVideos, false);

/**
 * Uploads an image file and adds it to the images store.
 * @param {File} file - The file to upload.
 * @returns {Promise<{success: boolean, image?: object, error?: string}>}
 */
export const addImage = async (file) => {
  try {
    const form = new FormData();
    form.append('file', file);

    const uploadResponse = await AssetsService.uploadFile(file.name, file.type, form);

    if (uploadResponse?.status >= 400) {
      return { success: false, error: uploadResponse.message };
    }

    const { hash, filesize, filename, metadata } = uploadResponse;

    const data = {
      hash,
      filesize,
      filename,
      metadata,
      inline: false,
      mimetype: metadata.mimetype,
      rulename: filename
    };

    const response = await AssetsService.addImage(data);

    if (response?.status >= 400) {
      return { success: false, error: response.message };
    }

    // Prepend the new image to the store
    images.update((current) => [response, ...current]);

    return { success: true, image: response };
  } catch (err) {
    console.error('Error uploading image:', err);
    return { success: false, error: err.message };
  }
};
